import dayjs from 'dayjs';
import { toString } from 'lodash';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import {
  PayrollCalendarFormFields,
  PayrollCalendarFrequency,
  PayrollCalendarFrequencyResponse,
  PayrollCalendarObject,
  PayrollCalendarParams,
} from '@/modules/employer/components/SetupEmployer/Features/PayrollCalendar/payrollCalendar.types';
import { PayrollCalendar, PayrollCalendarConfig } from '@/modules/Renewals/renewals.types';

/**
 * Frequency -> form day fields for the modal step 1 form.
 */
const getFormDayFields = (
  rawFrequency: string | undefined,
  config?: PayrollCalendarConfig,
  schedule?: string[]
): Partial<PayrollCalendarParams> => {
  const { firstDayOfMonth, secondDayOfMonth, dayOfWeek } = config ?? {};

  switch (rawFrequency) {
    case PayrollCalendarFrequencyResponse.MONTHLY:
      return firstDayOfMonth != null
        ? {
            [PayrollCalendarFormFields.DAY_OF_MONTH]: toString(firstDayOfMonth),
            [PayrollCalendarFormFields.SEMI_MONTHLY_DAY_OF_MONTH_1]: toString(firstDayOfMonth),
          }
        : {};
    case PayrollCalendarFrequencyResponse.WEEKLY:
      return dayOfWeek ? { [PayrollCalendarFormFields.DAY_OF_WEEK]: dayOfWeek } : {};
    case PayrollCalendarFrequencyResponse.BI_WEEKLY:
      return dayOfWeek ? { [PayrollCalendarFormFields.BI_WEEKLY_DAY_OF_WEEK]: dayOfWeek } : {};
    case PayrollCalendarFrequencyResponse.SEMI_MONTHLY:
      return {
        ...(firstDayOfMonth != null
          ? { [PayrollCalendarFormFields.SEMI_MONTHLY_DAY_OF_MONTH_1]: toString(firstDayOfMonth) }
          : {}),
        ...(secondDayOfMonth != null
          ? { [PayrollCalendarFormFields.SEMI_MONTHLY_DAY_OF_MONTH_2]: toString(secondDayOfMonth) }
          : {}),
      };
    case PayrollCalendarFrequencyResponse.ANNUALLY:
      return schedule?.[0]
        ? { [PayrollCalendarFormFields.DATE_OF_YEAR]: dayjs(schedule[0]).format(DEFAULT_DATE_FORMAT) }
        : {};
    default:
      return {};
  }
};

/**
 * Adapts a renewal PayrollCalendar into the minimal Partial<PayrollCalendarObject>
 * shape that the edit modal needs — only fields actually read by steps 1 and 2.
 */
export const adaptRenewalCalendarToEditable = (
  calendar: PayrollCalendar
): Partial<PayrollCalendarObject> & { config: PayrollCalendarParams } => {
  const raw = calendar.rawDto;
  const rawFrequency = calendar.config?.frequency || calendar.frequencyType || raw.frequency_type || '';
  const frequencyLabel =
    PayrollCalendarFrequency[rawFrequency as keyof typeof PayrollCalendarFrequency] || '';
  const startDateStr = calendar.config?.startDate
    ? dayjs(calendar.config.startDate).format(DEFAULT_DATE_FORMAT)
    : raw.config?.start_date
      ? dayjs(raw.config.start_date).format(DEFAULT_DATE_FORMAT)
      : '';

  return {
    id: calendar.id,
    endDate: raw.end_date ? String(raw.end_date) : '',
    schedule: calendar.schedule ?? (raw.schedule ? raw.schedule.map(String) : []),
    config: {
      calendarId: calendar.id,
      [PayrollCalendarFormFields.CALENDAR_NAME]: calendar.name ?? raw.name ?? '',
      [PayrollCalendarFormFields.FREQUENCY]: frequencyLabel,
      [PayrollCalendarFormFields.START_DATE]: startDateStr,
      ...getFormDayFields(rawFrequency, calendar.config, calendar.schedule),
    } as PayrollCalendarParams,
  };
};

/**
 * Converts a PayrollCalendarConfig (camelCase) to the snake_case shape the backend expects.
 * Only includes fields that are actually set — no stale fields from a previous frequency.
 */
export const toRawConfig = (config: PayrollCalendarConfig): Record<string, unknown> => ({
  ...(config.calendarId != null ? { calendar_id: config.calendarId } : {}),
  start_date: config.startDate,
  frequency: config.frequency,
  ...(config.dayOfWeek != null ? { day_of_week: config.dayOfWeek } : {}),
  ...(config.firstDayOfMonth != null ? { first_day_of_month: config.firstDayOfMonth } : {}),
  ...(config.secondDayOfMonth != null ? { second_day_of_month: config.secondDayOfMonth } : {}),
  ...(config.month != null ? { month: config.month } : {}),
  ...(config.dayOfMonth != null ? { day_of_month: config.dayOfMonth } : {}),
});

export default adaptRenewalCalendarToEditable;
